// src/components/admin/settings/AdminSettings.jsx
import { useState, useEffect } from 'react';
import { FaCog, FaSave, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import SiteSettings from './SiteSettings';
import { useSiteConfig } from '../../../hooks/useSiteConfig';
import { useAuth } from '../../../hooks/useAuth';

const AdministratorSettings = () => {
  const { user } = useAuth();
  const {
    siteConfig,
    updateSiteConfig,
    resetToDefaults: resetSiteConfigToDefaults,
    loading: siteConfigLoading,
    updating: siteConfigUpdating,
    error: siteConfigError,
  } = useSiteConfig();

  const [localSiteSettings, setLocalSiteSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Populate local state once siteConfig loads
  useEffect(() => {
    if (!siteConfig) return;
    setLocalSiteSettings((prev) =>
      Object.keys(prev).length === 0 ? siteConfig : prev
    );
  }, [siteConfig]);

  const handleSettingChange = (setting, value) => {
    setLocalSiteSettings((prev) => ({ ...prev, [setting]: value }));
    setHasChanges(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await updateSiteConfig(localSiteSettings, user?.uid || 'admin');
      if (!result.success) {
        setError(`Failed to save: ${result.error}`);
        setTimeout(() => setError(''), 4000);
        return;
      }
      setHasChanges(false);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to save settings');
      setTimeout(() => setError(''), 4000);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!window.confirm('Reset all settings to defaults? This cannot be undone.')) return;
    setSaving(true);
    try {
      const result = await resetSiteConfigToDefaults(user?.uid || 'admin');
      if (result.success) {
        setLocalSiteSettings(siteConfig);
        setHasChanges(false);
        setSuccess('Settings reset to defaults!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Some settings could not be reset');
        setTimeout(() => setError(''), 4000);
      }
    } catch {
      setError('Failed to reset settings');
      setTimeout(() => setError(''), 4000);
    } finally {
      setSaving(false);
    }
  };

  if (siteConfigLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <FaCog className="w-6 h-6 text-primary-600" />
            Settings
          </h1>
          <p className="text-sm text-gray-500 mt-1">Manage your site configuration</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={resetToDefaults}
            disabled={saving || siteConfigUpdating}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Reset to Defaults
          </button>
          <button
            onClick={saveSettings}
            disabled={saving || siteConfigUpdating || !hasChanges}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${
              hasChanges ? 'bg-primary-600 hover:bg-primary-700' : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Saving...
              </>
            ) : (
              <>
                <FaSave className="w-4 h-4" />
                {hasChanges ? 'Save Changes' : 'No Changes'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Success */}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <FaCheck className="w-4 h-4 text-green-500 flex-shrink-0" />
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Unsaved changes warning */}
      {hasChanges && !success && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <FaExclamationTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
          <p className="text-sm text-yellow-700">You have unsaved changes — click Save Changes to apply them.</p>
        </div>
      )}

      {/* Error */}
      {(error || siteConfigError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <FaExclamationTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error || siteConfigError}</p>
        </div>
      )}

      {/* Site Settings — rendered directly, no tabs */}
      <div className="bg-white shadow rounded-lg p-6">
        <SiteSettings
          settings={localSiteSettings}
          onSettingChange={handleSettingChange}
          loading={saving || siteConfigUpdating}
        />
      </div>
    </div>
  );
};

export default AdministratorSettings;